// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Starter Custom Code - Start */
import PlusCircleFill from "@store/Library/Components/Toolkit/Icons/PlusCircle/PlusCircleFill";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { useIsPunchOutSession } from "@toolkit/Framework/Store/Context/ContextHooks";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import addToWishListWithKit from "@store/Framework/Store/Store/Data/WishLists/Handlers/AddToWishListWithKit";
import { KitProductInfo } from "@store/Framework/Types/KitProductInfo";
import { getKitSelectionState } from "@store/Framework/Utilities/KitProductInfoUtilities";
import { css } from "styled-components";
/* Store Custom Code - End */

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ProductAddToListLinkStyles;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

/* Store Custom Code - Start */
const mapStateToProps = (state: ApplicationState) => {
    const settings = getSettingsCollection(state);

    return {
        wishListSettings: settings.wishListSettings,
        productDetails: state.pages.productDetails,
        productKitSettings: settings.productKitSettings,
    };
};
/* Store Custom Code - End */

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    // Store Custom Code
    addToWishListWithKit,
};

export interface ProductAddToListLinkStyles {
    link?: LinkPresentationProps;
}

export const productAddToListLinkStyles: ProductAddToListLinkStyles = {
    /* Store Custom Code - Start */
    link: mergeProps<ProductAddToListLinkStyles["link"]>(linkGetActionLinkProps(PlusCircleFill), {
        css: css`
            &:disabled {
                cursor: not-allowed !important;
            }
        `,
    }),
    /* Store Custom Code - End */
};

/**
 * @deprecated Use productAddToListLinkStyles instead.
 */
export const productAddToListLink = productAddToListLinkStyles;

const ProductAddToListLink: React.FC<Props> = ({
    productContext: { product, productInfo },
    wishListSettings,
    labelOverride,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    // Store Custom Code
    productDetails,
    productKitSettings,
    addToWishListWithKit,
    ...otherProps
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(productAddToListLink, extendedStyles));
    const isPunchOutSession = useIsPunchOutSession(); // Starter Custom Code

    if (!product.canAddToWishlist || isPunchOutSession /* Starter Custom Code */) {
        return null;
    }

    /* Store Custom Code - Start */
    const allowPartialKitAddToCart = productKitSettings.allowPartialBundleAddToCart;
    const configSections =
        productDetails.selectedProductId === product.id && product.detail?.configuration?.isKit === false
            ? product.detail.configuration.configSections
            : undefined;
    const currentConfigurationSelection = configSections?.length ? productDetails.configurationSelection : undefined;
    const isPartialKitConfiguration = getKitSelectionState(configSections, currentConfigurationSelection) === "partial";
    const isKitConfigurationIncomplete = isPartialKitConfiguration && !allowPartialKitAddToCart;

    const addToListLinkClickHandler = () => {
        if (isKitConfigurationIncomplete) {
            return;
        }

        const kitProductInfos: KitProductInfo[] = [{ ...productInfo, kitConfiguration: currentConfigurationSelection }];

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishListWithKit({
                productInfos: kitProductInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result?.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result?.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: kitProductInfos });
    };
    /* Store Custom Code - End */

    return (
        <Link
            {...styles.link}
            onClick={addToListLinkClickHandler}
            disabled={productInfo.qtyOrdered <= 0 || isKitConfigurationIncomplete /* Store Custom Code */}
            {...otherProps}
        >
            {labelOverride ?? translate("Add to List")}
        </Link>
    );
};

export default withProductContext(
    connect(mapStateToProps, mapDispatchToProps)(ProductAddToListLink),
) /* Store Custom Code */;
